import { format } from "date-fns";
import { de } from "date-fns/locale";
import {
  Trash2, Check, FileDown, FileUp, Layers, X, Loader2, AlertTriangle, Clock,
  RefreshCw, ToggleLeft, ToggleRight, Zap, CalendarClock,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { DELTA_COLORS } from "./constants";

// ============ ASSIGNMENT DIALOG ============
export function AssignmentDialog({
  open, onOpenChange, selectedAssignment, selectedDate,
  formData, setFormData, users, projects, onSave, onDeleteOpen,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[13px]">
            {selectedAssignment ? "Einsatz bearbeiten" : "Neuer Einsatz"}
            {selectedDate && (
              <span className="ml-2 text-[11px] font-normal text-gray-500">
                {format(selectedDate, "EEE d. MMM", { locale: de })}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {!selectedAssignment && (
            <div>
              <Label className="text-[11px] text-gray-500">Mitarbeiter</Label>
              <Select value={formData.user_id} onValueChange={(v) => setFormData({ ...formData, user_id: v })}>
                <SelectTrigger className="mt-0.5 h-8" data-testid="form-user"><SelectValue placeholder="MA wählen" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-[11px] text-gray-500">Projekt</Label>
            <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
              <SelectTrigger className="mt-0.5 h-8" data-testid="form-project"><SelectValue placeholder="Projekt" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color || "#6B7280" }} />
                      {p.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-gray-500">Geplante Stunden</Label>
            <Input type="number" min="0.5" max="24" step="0.5" value={formData.planned_hours}
              onChange={(e) => setFormData({ ...formData, planned_hours: parseFloat(e.target.value) || 0 })}
              className="mt-0.5 h-8" data-testid="form-hours" />
          </div>
          <div>
            <Label className="text-[11px] text-gray-500">Status</Label>
            <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
              <SelectTrigger className="mt-0.5 h-8" data-testid="form-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="planned"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500" />Geplant</span></SelectItem>
                <SelectItem value="confirmed"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500" />Bestätigt</span></SelectItem>
                <SelectItem value="blocked"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-purple-500" />BLOCK</span></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-gray-500">Notizen</Label>
            <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="z.B. Montage, Service..." className="mt-0.5 h-8" data-testid="form-notes" />
          </div>
          {selectedAssignment?.actual_hours !== undefined && (
            <div className="bg-gray-50 p-2 text-[11px]" style={{ borderRadius: 1 }}>
              <div className="flex items-center gap-1 font-medium text-gray-700 mb-1"><Clock className="w-3 h-3" />Soll/Ist</div>
              <div className="grid grid-cols-3 gap-3">
                <div><span className="text-gray-500">Soll</span><div className="font-semibold">{selectedAssignment.planned_hours}h</div></div>
                <div><span className="text-gray-500">Ist</span><div className="font-semibold">{selectedAssignment.actual_hours || 0}h</div></div>
                <div><span className="text-gray-500">Delta</span><div className={`font-semibold ${DELTA_COLORS[selectedAssignment.delta_status]?.text || ""}`}>{selectedAssignment.delta_hours > 0 ? "+" : ""}{selectedAssignment.delta_hours || 0}h</div></div>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between pt-2 border-t">
          {selectedAssignment ? (
            <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 text-[11px]"
              onClick={() => onDeleteOpen(true)} data-testid="delete-assignment">
              <Trash2 className="w-3 h-3 mr-1" />Löschen
            </Button>
          ) : <div />}
          <div className="flex gap-1.5">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="h-8 text-[11px]">Abbrechen</Button>
            <Button onClick={onSave} className="h-8 text-[11px]" data-testid="save-assignment"><Check className="w-3 h-3 mr-1" />Vormerken</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ QUICK DELETE DIALOG ============
export function QuickDeleteDialog({ target, onClose, onConfirm }) {
  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-xs">
        <DialogHeader>
          <DialogTitle className="text-[13px]">Einsatz löschen?</DialogTitle>
          <DialogDescription className="text-[11px]">
            {target && (<><strong>{target.project_name}</strong> {target.planned_hours}h {target.date && format(new Date(target.date), "dd.MM.yyyy")}</>)}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-1.5">
          <Button variant="outline" onClick={onClose} className="h-8 text-[11px]">Abbrechen</Button>
          <Button variant="destructive" onClick={onConfirm} className="h-8 text-[11px]" data-testid="confirm-quick-delete">Löschen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ DELETE CONFIRM DIALOG ============
export function DeleteConfirmDialog({ open, onOpenChange, onDelete }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[13px]">Einsatz löschen?</AlertDialogTitle>
          <AlertDialogDescription className="text-[11px]">Wird zum Löschen vorgemerkt.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="h-8 text-[11px]">Abbrechen</AlertDialogCancel>
          <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700 h-8 text-[11px]">Löschen</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ============ DISCARD DIALOG ============
export function DiscardDialog({ open, onOpenChange, onDiscard, modifiedCount, createdCount, deletedCount }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-1.5 text-[13px]"><AlertTriangle className="w-4 h-4 text-yellow-600" />Alle Änderungen verwerfen?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-[11px]">
              <ul className="list-disc list-inside">
                {modifiedCount > 0 && <li>{modifiedCount} geändert</li>}
                {createdCount > 0 && <li>{createdCount} neu</li>}
                {deletedCount > 0 && <li>{deletedCount} gelöscht</li>}
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="h-8 text-[11px]">Abbrechen</AlertDialogCancel>
          <AlertDialogAction onClick={onDiscard} className="bg-red-600 hover:bg-red-700 h-8 text-[11px]">Verwerfen</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ============ TEMPLATE DIALOGS ============
export function SaveTemplateDialog({ open, onOpenChange, templateName, setTemplateName, onSave, activeCalendarWeek }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5 text-[13px]"><Layers className="w-4 h-4" />Vorlage speichern (KW {activeCalendarWeek})</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <Label className="text-[11px] text-gray-500">Name</Label>
          <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="z.B. Standard-Woche" className="mt-0.5 h-8" />
        </div>
        <div className="flex justify-end gap-1.5 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-8 text-[11px]">Abbrechen</Button>
          <Button onClick={onSave} className="h-8 text-[11px]"><FileDown className="w-3 h-3 mr-1" />Speichern</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ApplyTemplateDialog({ open, onOpenChange, templates, selectedTemplateId, setSelectedTemplateId, targetWeekStart, setTargetWeekStart, onApply }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5 text-[13px]"><FileUp className="w-4 h-4" />Vorlage anwenden</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[11px] text-gray-500">Vorlage</Label>
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger className="mt-0.5 h-8"><SelectValue placeholder="Vorlage wählen" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.entries?.length || 0})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-gray-500">Zielwoche (Montag)</Label>
            <Input type="date" value={targetWeekStart} onChange={(e) => setTargetWeekStart(e.target.value)} className="mt-0.5 h-8" />
          </div>
        </div>
        <div className="flex justify-end gap-1.5 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-8 text-[11px]">Abbrechen</Button>
          <Button onClick={onApply} className="h-8 text-[11px]"><FileUp className="w-3 h-3 mr-1" />Anwenden</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ PDF EXPORT DIALOG ============
export function PdfExportDialog({ open, onOpenChange, pdfDateRange, setPdfDateRange, pdfUseFilters, setPdfUseFilters, filterUserIds, pdfExportLoading, onExport }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5 text-[13px]"><FileDown className="w-4 h-4" />PDF Export</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px]">Von</Label><Input type="date" value={pdfDateRange.start} onChange={(e) => setPdfDateRange(p => ({ ...p, start: e.target.value }))} className="h-8" /></div>
            <div><Label className="text-[11px]">Bis</Label><Input type="date" value={pdfDateRange.end} onChange={(e) => setPdfDateRange(p => ({ ...p, end: e.target.value }))} className="h-8" /></div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="pdf-filters" checked={pdfUseFilters} onChange={(e) => setPdfUseFilters(e.target.checked)} className="w-3.5 h-3.5" />
            <Label htmlFor="pdf-filters" className="cursor-pointer text-[11px]">
              Filter berücksichtigen {filterUserIds.length > 0 && <span className="text-gray-400">({filterUserIds.length} MA)</span>}
            </Label>
          </div>
        </div>
        <div className="flex justify-end gap-1.5">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-8 text-[11px]">Abbrechen</Button>
          <Button onClick={onExport} disabled={pdfExportLoading} className="h-8 text-[11px]">
            {pdfExportLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Erstelle...</> : <><FileDown className="w-3 h-3 mr-1" />PDF</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ RECURRING TEMPLATES MANAGER DIALOG ============
export function RecurringTemplatesDialog({
  open, onOpenChange, templates, onToggleRecurring, onGenerateNext,
  onDeleteTemplate, generatingId,
}) {
  const recurringTemplates = templates.filter(t => t.recurring && t.is_active);
  const otherTemplates = templates.filter(t => !t.recurring || !t.is_active);

  const INTERVAL_LABELS = { weekly: "Woechentlich", biweekly: "Alle 2 Wochen" };
  const DAY_NAMES = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

  const getTemplateDaySummary = (template) => {
    const days = new Set((template.entries || []).map(e => e.day_offset));
    return [...days].sort().map(d => DAY_NAMES[d] || `T${d}`).join(", ");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <CalendarClock className="w-4 h-4 text-blue-600" />
            Wochenvorlagen verwalten
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            Wiederkehrende Vorlagen generieren automatisch Einsaetze fuer die naechste Woche.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Active recurring */}
          {recurringTemplates.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <RefreshCw className="w-3.5 h-3.5 text-green-600" />
                <span className="text-[12px] font-semibold text-gray-800">Aktive Vorlagen</span>
              </div>
              <div className="space-y-2">
                {recurringTemplates.map(t => (
                  <div key={t.id} className="border border-green-200 bg-green-50/50 p-3" style={{ borderRadius: 2 }}
                    data-testid={`recurring-template-${t.id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-semibold text-gray-900">{t.name}</span>
                        <span className="px-1.5 py-0.5 text-[9px] font-medium bg-green-100 text-green-700 rounded-sm">
                          {INTERVAL_LABELS[t.recurring_interval] || "Woechentlich"}
                        </span>
                      </div>
                      <button
                        onClick={() => onToggleRecurring(t.id, false)}
                        className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-red-600 transition-colors"
                        data-testid={`deactivate-${t.id}`}
                      >
                        <ToggleRight className="w-4 h-4 text-green-600" />
                        Deaktivieren
                      </button>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-gray-500">
                      <span>{t.entries?.length || 0} Eintraege</span>
                      <span>Tage: {getTemplateDaySummary(t)}</span>
                      {t.last_applied_date && <span>Zuletzt: {t.last_applied_date}</span>}
                      {t.next_apply_date && <span className="text-blue-600 font-medium">Naechste: {t.next_apply_date}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-[10px] px-2 bg-white"
                        onClick={() => onGenerateNext(t.id)}
                        disabled={generatingId === t.id}
                        data-testid={`generate-next-${t.id}`}
                      >
                        {generatingId === t.id
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Erstelle...</>
                          : <><Zap className="w-3 h-3 mr-1" />Jetzt generieren</>
                        }
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-[10px] px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => onDeleteTemplate(t.id)}
                        data-testid={`delete-template-${t.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inactive/non-recurring */}
          {otherTemplates.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Layers className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-[12px] font-semibold text-gray-800">Einmalige Vorlagen</span>
              </div>
              <div className="space-y-2">
                {otherTemplates.map(t => (
                  <div key={t.id} className="border border-gray-200 bg-white p-3" style={{ borderRadius: 2 }}
                    data-testid={`template-${t.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[12px] font-medium text-gray-800">{t.name}</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onToggleRecurring(t.id, true, "weekly")}
                          className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-green-600 transition-colors"
                          data-testid={`activate-weekly-${t.id}`}
                        >
                          <ToggleLeft className="w-4 h-4" />
                          Woechentlich
                        </button>
                        <button
                          onClick={() => onToggleRecurring(t.id, true, "biweekly")}
                          className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-green-600 transition-colors"
                          data-testid={`activate-biweekly-${t.id}`}
                        >
                          <ToggleLeft className="w-4 h-4" />
                          2-Woechentlich
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-gray-500">
                      <span>{t.entries?.length || 0} Eintraege</span>
                      <span>Tage: {getTemplateDaySummary(t)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-[10px] px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => onDeleteTemplate(t.id)}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />Loeschen
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {templates.length === 0 && (
            <div className="text-center py-6 text-[12px] text-gray-400">
              Noch keine Vorlagen vorhanden. Speichern Sie die aktuelle Woche als Vorlage.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-8 text-[11px]">Schliessen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
